import { DateTime } from 'luxon';
import { catchException } from './catchException';
export { DateTime };
export const parseDateTime = (date) => date && DateTime.fromISO(date).isValid ? DateTime.fromISO(date) : null;
export const parseJSDateTime = (date) => date && DateTime.fromJSDate(date).isValid ? DateTime.fromJSDate(date) : null;
export const getCurrentDate = () => {
    const testDate = '2022-12-07T12:00:00+05:30';
    const env = process.env.NODE_ENV;
    if (env === 'test' || process.env.STORYBOOK_APP_ENV === 'storybook') {
        return parseDateTime(testDate) || DateTime.now();
    }
    return DateTime.now();
};
export const formatDate = (dateString) => {
    const date = parseDateTime(dateString);
    if (!date)
        return 'Invalid date (formatDate)';
    const { day } = date;
    const month = date.monthLong;
    const weekday = date.weekdayLong;
    // const month = date.toFormat('MMMM'); // Full month name (e.g., "January")
    // const weekday = date.toFormat('cccc'); // Full weekday name (e.g., "Saturday")
    return `${day} ${month}, ${weekday}`;
};
export const formatIsoDateToLongDate = (isoDate, showYear = true) => {
    const date = parseDateTime(isoDate);
    if (!date)
        return 'Invalid ISO Date';
    return showYear ? date.toFormat('d MMMM yyyy') : date.toFormat('d MMMM');
};
export const formatTimeRangeInIST = (startDate, endDate) => {
    const start = parseDateTime(startDate);
    const end = parseDateTime(endDate);
    if (!start || !end) {
        return 'Invalid date range';
    }
    const startTime = start.toFormat('h:mm a');
    const endTime = end.toFormat('h:mm a');
    const zone = start.toFormat('ZZZZ');
    return `${startTime} - ${endTime} ${getMappedGmtTimezoneToShortName(zone)}`;
};
export const getTimeDifferenceInHoursAndMinutes = ({ from, to }) => {
    const fromDate = parseDateTime(from);
    const toDate = parseDateTime(to);
    if (!fromDate || !toDate || !fromDate.isValid || !toDate.isValid || toDate < fromDate) {
        return 'Invalid time range';
    }
    const diff = toDate.diff(fromDate, ['hours', 'minutes']).toObject();
    const hours = Math.floor(diff.hours ?? 0);
    const minutes = Math.floor(diff.minutes ?? 0);
    const hourStr = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
    const minuteStr = minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''}` : '';
    return [hourStr, minuteStr].filter(Boolean).join(' ');
};
export function getMappedGmtTimezoneToShortName(zone) {
    if (zone === 'GMT+5:30' || zone === 'Asia/Kolkata' || zone === 'Asia/Calcutta') {
        return 'IST';
    }
    return zone;
}
export const getCurrentTimeInUnit = (unit = 'seconds') => {
    const now = getCurrentDate().toMillis(); // Get the current time in milliseconds
    switch (unit) {
        case 'milliseconds':
            return now; // Already in milliseconds
        case 'minutes':
            return Math.floor(now / (60 * 1000)); // Convert milliseconds to minutes
        case 'hours':
            return Math.floor(now / (60 * 60 * 1000)); // Convert milliseconds to hours
        case 'seconds':
        default:
            return Math.floor(now / 1000); // Convert milliseconds to seconds
    }
};
export const convertHoursToUnit = (hours, unit = 'milliseconds') => {
    const MS_IN_HOUR = 60 * 60 * 1000;
    const ms = hours * MS_IN_HOUR;
    switch (unit) {
        case 'milliseconds':
            return ms;
        case 'seconds':
            return ms / 1000;
        case 'minutes':
            return ms / (60 * 1000);
        case 'hours':
            return hours;
        case 'days':
            return hours / 24;
        case 'weeks':
            return hours / (24 * 7);
        case 'months': {
            const now = getCurrentDate();
            return now.plus({ hours }).diff(now, 'months').months;
        }
        case 'years': {
            const now = getCurrentDate();
            return now.plus({ hours }).diff(now, 'years').years;
        }
        default:
            throw new Error(`Unsupported unit: ${unit}`);
    }
};
// Normalize "Asia/Calcutta" to "Asia/Kolkata": both are identical UTC+05:30 zones,
// but browsers/OS tzdata often return the legacy alias "Calcutta" (pre-2001 city name).
// Explicitly mapping it ensures consistent timezone keys across the app.
const detectedTimezone = DateTime.now().zoneName;
export const systemTimezone = detectedTimezone === 'Asia/Calcutta' ? 'Asia/Kolkata' : detectedTimezone;
export function formatCustomLongDateWithEndTime(dateString, timeZone, separator = 'at') {
    if (!dateString) {
        return `Invalid Date string ${dateString}`;
    }
    const date = parseDateTime(dateString)?.setZone(timeZone || systemTimezone);
    if (!date || !date.isValid) {
        return `Invalid Date string ${dateString}`;
    }
    // Format the date as "DD MM YYYY at H:mm AM/PM"
    // const formattedDate = date.toFormat("dd LLLL yyyy 'at' h:mm a");
    const formattedDate = `${date.toFormat('dd LLLL yyyy')} ${separator} ${date.toFormat('h:mm a')}`;
    // Add timezone abbreviation
    const timezoneAbbreviation = getMappedGmtTimezoneToShortName(timeZone || systemTimezone);
    return `${formattedDate} ${timezoneAbbreviation}`;
}
export const getTimezoneLabel = (dateString, formatType = 'long', fallbackTimezone = systemTimezone) => {
    const parsedDate = parseDateTime(dateString);
    const zone = parsedDate?.toFormat('ZZZZ');
    const shortTimezoneName = getMappedGmtTimezoneToShortName(zone || fallbackTimezone);
    if (formatType === 'short') {
        return shortTimezoneName;
    }
    return getTimeZoneFullName(shortTimezoneName);
};
export function getTimeZoneFullName(abbreviation) {
    const timeZones = {
        ADT: 'Atlantic Daylight Time',
        AKDT: 'Alaska Daylight Time',
        AKST: 'Alaska Standard Time',
        AST: 'Atlantic Standard Time',
        CDT: 'Central Daylight Time',
        CST: 'Central Standard Time',
        EDT: 'Eastern Daylight Time',
        EST: 'Eastern Standard Time',
        FJT: 'Fiji Time',
        GMT: 'Greenwich Mean Time',
        HADT: 'Hawaii-Aleutian Daylight Time',
        HAST: 'Hawaii-Aleutian Standard Time',
        IST: 'India Standard Time',
        JST: 'Japan Standard Time',
        MDT: 'Mountain Daylight Time',
        MST: 'Mountain Standard Time',
        NDT: 'Newfoundland Daylight Time',
        NST: 'Newfoundland Standard Time',
        PDT: 'Pacific Daylight Time',
        PST: 'Pacific Standard Time',
        SDT: 'Samoa Daylight Time',
        SST: 'Samoa Standard Time',
        UTC: 'Coordinated Universal Time',
    };
    return timeZones[abbreviation] || abbreviation;
}
export const getMinMaxDateHelper = (param) => {
    const today = getCurrentDate();
    if (typeof param === 'number') {
        const minDate = today.toJSDate();
        const maxDate = today.plus({ days: param }).toJSDate();
        return { minDate, maxDate };
    }
    if (typeof param === 'object') {
        const { minDate, maxDate, days } = param;
        if (minDate) {
            return today.toJSDate();
        }
        if (maxDate) {
            return today.plus({ days: days || 0 }).toJSDate();
        }
    }
    // return today.toJSDate();
    throw catchException.validationError('Invalid input for getMinMaxDate', {
        param,
        expected: "number or an object with 'minDate' or 'maxDate' keys",
    });
};
// Example Usage
// console.log(getMinMaxDate(7));
// Output: { minDate: [current_date], maxDate: [current_date + 7 days] }
// console.log(getMinMaxDate({ minDate: true }));
// Output: [current_date]
// console.log(getMinMaxDate({ maxDate: true, days: 7 }));
// Output: [current_date + 7 days]
export const isSlotTimeStarted = (slotDateTime) => {
    if (!slotDateTime)
        return false;
    const currentDate = getCurrentDate();
    const slotStartDateTime = parseDateTime(slotDateTime);
    return !!(slotStartDateTime && currentDate && slotStartDateTime < currentDate);
};
export function getTimeZoneName(timeZoneId, locale = 'en-US') {
    try {
        const date = new Date();
        const options = {
            timeZoneName: 'long',
            timeZone: timeZoneId,
        };
        const formatted = new Intl.DateTimeFormat(locale, options)
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName');
        return formatted?.value || timeZoneId;
    }
    catch (e) {
        return timeZoneId;
    }
}
export const formatTimezone = (zone) => {
    if (!zone)
        return '';
    if ((zone || '').toLowerCase().includes('calcutta')) {
        return 'Asia/Kolkata';
    }
    // ICU on UTC-configured systems (e.g. CI runners) reports the zone as bare "UTC",
    // but the scheduler backend's timezone whitelist only recognizes the IANA name "Etc/UTC".
    if (zone === 'UTC') {
        return 'Etc/UTC';
    }
    return zone;
};
export const serializeSlotDate = (date) => {
    const parsedDate = parseJSDateTime(date);
    if (!parsedDate)
        return '';
    // Convert the instant to UTC; `keepLocalTime` would relabel the local clock time as UTC.
    const dateTime = parsedDate.toUTC();
    if (!dateTime.isValid) {
        return date.toISOString();
    }
    return dateTime.toISO({ suppressMilliseconds: true });
};
export const stripIsoMilliseconds = (value) => {
    if (!value)
        return '';
    return value.replace(/\.\d+(?=(Z|[+-]\d{2}:\d{2}))/i, '');
};
